import { useState, useEffect } from "react";
import { useAuth, API, USER_ROLES, ORG_TYPES } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Users, UserPlus, MoreHorizontal, Shield, Mail, 
  Building2, Clock, CheckCircle2, XCircle, AlertCircle,
  UserCog, Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const statusColors = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  invited: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  deactivated: "bg-red-500/20 text-red-400 border-red-500/30"
};

const statusIcons = {
  active: CheckCircle2,
  invited: Clock,
  deactivated: XCircle
};

export default function UserManagement() {
  const { user, organization } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");
  const [inviting, setInviting] = useState(false);

  // Edit form state
  const [editRole, setEditRole] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/org/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteName.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setInviting(true);
    try {
      await axios.post(
        `${API}/org/users/invite`,
        { email: inviteEmail, name: inviteName, role: inviteRole },
        { withCredentials: true }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteName("");
      setInviteRole("staff");
      fetchUsers();
    } catch (error) {
      console.error("Invite error:", error);
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleEditUser = (targetUser) => {
    setSelectedUser(targetUser);
    setEditRole(targetUser.role);
    setEditOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API}/org/users/${selectedUser.user_id}`,
        { role: editRole },
        { withCredentials: true }
      );
      toast.success("User updated successfully");
      setEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeactivateUser = async (targetUser) => {
    if (!confirm(`Are you sure you want to deactivate ${targetUser.name}?`)) return;

    try {
      await axios.delete(`${API}/org/users/${targetUser.user_id}`, { withCredentials: true });
      toast.success(`${targetUser.name} has been deactivated`);
      fetchUsers();
    } catch (error) {
      console.error("Deactivate error:", error);
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const handleReactivateUser = async (targetUser) => {
    try {
      await axios.put(
        `${API}/org/users/${targetUser.user_id}`,
        { status: "active" },
        { withCredentials: true }
      );
      toast.success(`${targetUser.name} has been reactivated`);
      fetchUsers();
    } catch (error) {
      console.error("Reactivate error:", error);
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const activeUsers = users.filter(u => u.status === "active").length;
  const invitedUsers = users.filter(u => u.status === "invited").length;
  const deactivatedUsers = users.filter(u => u.status === "deactivated").length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="user-management-title">
              User Management
            </h1>
            <p className="text-slate-400 mt-1">
              Manage team members for {organization?.name || "your organization"}
            </p>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="invite-user-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">Invite Team Member</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Send an invitation to join your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Address</Label>
                  <Input
                    data-testid="invite-email-input"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    className="bg-slate-900/50 border-slate-600 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Full Name</Label>
                  <Input
                    data-testid="invite-name-input"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Doe"
                    className="bg-slate-900/50 border-slate-600 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger 
                      data-testid="invite-role-select"
                      className="bg-slate-900/50 border-slate-600 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(USER_ROLES).map(([role, { label, description }]) => (
                        <SelectItem 
                          key={role} 
                          value={role}
                          className="text-slate-300 focus:bg-slate-700 focus:text-white"
                        >
                          <div>
                            <span className="font-medium">{label}</span>
                            <span className="text-slate-500 ml-2 text-sm">- {description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setInviteOpen(false)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    data-testid="send-invite-btn"
                    disabled={inviting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {inviting ? "Sending..." : "Send Invitation"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-700 rounded-lg">
                  <Users className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.length}</p>
                  <p className="text-sm text-slate-400">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{activeUsers}</p>
                  <p className="text-sm text-slate-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{invitedUsers}</p>
                  <p className="text-sm text-slate-400">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{deactivatedUsers}</p>
                  <p className="text-sm text-slate-400">Deactivated</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organization Info Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-emerald-400" />
              <div>
                <CardTitle className="text-white">{organization?.name}</CardTitle>
                <CardDescription className="text-slate-400">
                  {ORG_TYPES[organization?.org_type]?.label || "Organization"} • 
                  Status: <span className="text-emerald-400 capitalize">{organization?.status}</span>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Users Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Team Members</CardTitle>
            <CardDescription className="text-slate-400">
              All users with access to your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-slate-400">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400">No users found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const StatusIcon = statusIcons[u.status] || AlertCircle;
                    const isCurrentUser = u.user_id === user.user_id;
                    
                    return (
                      <TableRow 
                        key={u.user_id} 
                        className="border-slate-700"
                        data-testid={`user-row-${u.user_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img 
                                src={u.picture} 
                                alt={u.name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                <span className="text-slate-300 text-sm font-medium">
                                  {u.name?.charAt(0)?.toUpperCase() || "?"}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="text-white font-medium">
                                {u.name}
                                {isCurrentUser && (
                                  <span className="text-emerald-400 text-xs ml-2">(You)</span>
                                )}
                              </p>
                              <p className="text-slate-400 text-sm">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className="border-slate-600 text-slate-300"
                          >
                            <Shield className="w-3 h-3 mr-1" />
                            {USER_ROLES[u.role]?.label || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[u.status]} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {u.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-slate-400 hover:text-white hover:bg-slate-700"
                                disabled={isCurrentUser}
                                data-testid={`user-actions-${u.user_id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent 
                              align="end"
                              className="bg-slate-800 border-slate-700"
                            >
                              <DropdownMenuItem 
                                onClick={() => handleEditUser(u)}
                                className="text-slate-300 focus:bg-slate-700 focus:text-white"
                              >
                                <UserCog className="w-4 h-4 mr-2" />
                                Edit Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-slate-700" />
                              {u.status === "deactivated" ? (
                                <DropdownMenuItem 
                                  onClick={() => handleReactivateUser(u)}
                                  className="text-emerald-400 focus:bg-slate-700 focus:text-emerald-300"
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => handleDeactivateUser(u)}
                                  className="text-red-400 focus:bg-slate-700 focus:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Edit User</DialogTitle>
              <DialogDescription className="text-slate-400">
                Update role for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(USER_ROLES).map(([role, { label, description }]) => (
                      <SelectItem 
                        key={role} 
                        value={role}
                        className="text-slate-300 focus:bg-slate-700 focus:text-white"
                      >
                        <div>
                          <span className="font-medium">{label}</span>
                          <span className="text-slate-500 ml-2 text-sm">- {description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditOpen(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateUser}
                disabled={updating}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {updating ? "Updating..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
